import { initializeApplication as baseInitializeApplication } from '../index';

/**
 * @typedef {Object} BannerElementsFromDOM
 *
 * @property {HTMLElement} bannerElement - The main banner element.
 * @property {HTMLElement} innerBannerElement - The inner banner element.
 * @property {HTMLElement} bannerTextElement - The element displaying the banner text.
 * @property {HTMLElement} remainingTimeElement - The element displaying the remaining time.
 * @property {HTMLElement} warningText1Element - The first warning text element.
 * @property {HTMLElement} warningText2Element - The second warning text element.
 * @property {HTMLElement} warningText3Element - The third warning text element.
 * @property {HTMLElement} warningText4Element - The fourth warning text element (last minute).
 * @property {HTMLElement} closedTextElement - The element displaying the closed text.
 */

/**
 * @typedef {Object} CloseAndWarningTimes
 *
 * @property {Date} close - The close time.
 * @property {number} warningSeconds - The number of seconds to display the first warning.
 * @property {number} finalWarningSeconds - The number of seconds to display the final warning.
 */

/**
 * @typedef {BannerElementsFromDOM & CloseAndWarningTimes} Options
 */

/**
 * The interval to check the remaining time in milliseconds
 * @private
 */
const CHECK_INTERVAL_MS = 1000;

/**
 * Flags used to avoid repeating work on the DOM
 * @private
 */
let flags = {
    hasClassHidden: false,
    hasClassFinal: false,
};

/**
 * Sets a flag to a value.
 *
 * @param {string} flag - The flag to set.
 * @param {boolean} value - The value to set the flag to.
 * @returns {void}
 */
export function setFlag(flag, value) {
    flags = {
        ...flags,
        [flag]: value,
    };
}

/**
 * Gets a flag value.
 *
 * @param {string} flag
 * @returns {boolean}
 */
export function getFlag(flag) {
    return flags[flag];
}

/**
 * Retrieves elements from the DOM.
 *
 * @returns {BannerElementsFromDOM | null} An object containing the elements or null if the root element is not found.
 */
export function getElementsFromDom() {
    const bannerElement = document.getElementById('out_of_hours_banner');

    // Return early if no banner element is found.
    if (bannerElement == null) {
        return null;
    }

    const innerBannerElement = document.getElementById('out_of_hours_banner_inner');
    const bannerTextElement = document.getElementById('out_of_hours_banner_text');
    const remainingTimeElement = document.getElementById('out_of_hours_banner_time_remaining_time');
    const warningText1Element = document.getElementById('out_of_hours_banner_time_remaining_warning');
    const warningText2Element = document.getElementById('out_of_hours_banner_time_remaining_warning2');
    const warningText3Element = document.getElementById('out_of_hours_banner_time_remaining_warning3');
    const warningText4Element = document.getElementById('out_of_hours_banner_time_remaining_warning4');
    const closedTextElement = document.getElementById('out_of_hours_banner_closed_text');

    return {
        bannerElement,
        innerBannerElement,
        bannerTextElement,
        remainingTimeElement,
        warningText1Element,
        warningText2Element,
        warningText3Element,
        warningText4Element,
        closedTextElement,
    };
}

/**
 * Retrieves close and warning times from the remaining time element.
 *
 * @param {HTMLElement | null} remainingTimeElement - The element containing the close and warning times.
 * @returns {CloseAndWarningTimes} An object containing the close time, warning seconds, and final warning seconds.
 */
export function getCloseAndWarningTimes(remainingTimeElement) {
    const dateString = remainingTimeElement?.dataset?.closeTime || '';
    const closeDate = dateString ? new Date(dateString) : new Date();

    const warningSecondsValue = dateString ? parseInt(remainingTimeElement.dataset.warningSeconds, 10) : 0;

    const finalWarningSecondsValue = dateString ? parseInt(remainingTimeElement.dataset.finalWarningSeconds, 10) : 0;

    // Ensure the values are valid and if not, set them to defaults.
    const close = Number.isNaN(closeDate) ? new Date() : closeDate;
    const warningSeconds = Number.isNaN(warningSecondsValue) ? 0 : warningSecondsValue;
    const finalWarningSeconds = Number.isNaN(finalWarningSecondsValue) ? 0 : finalWarningSecondsValue;

    return {
        close,
        warningSeconds,
        finalWarningSeconds,
    };
}

/**
 * Calculates the seconds remaining until the close time.
 *
 * @param {Date} close - The close time.
 * @returns {number} The number of seconds remaining.
 */
export function getSecondsRemaining(close) {
    const now = new Date();
    return Math.floor((close - now) / 1000);
}

/**
 * Formats the remaining time as MM or SS depending on time remaining.
 * If 59 seconds or less, displays seconds only (SS).
 * If more than 59 seconds, displays minutes only (MM).
 *
 * @param {number} secondsRemaining - The number of seconds remaining.
 * @returns {string} The time remaining formatted as `MM` or `SS`.
 */
export function getTimeRemainingText(secondsRemaining) {
    if (secondsRemaining <= 59) {
        // Display seconds only when 59 seconds or less
        const secondsText = secondsRemaining.toString();
        return secondsText;
    }
    // Display minutes only when more than 59 seconds
    const minutes = Math.ceil(secondsRemaining / 60);
    const minutesText = minutes.toString();
    return minutesText;
}

/**
 * Updates the text content of the remaining time element.
 *
 * @param {HTMLElement} remainingTimeElement - The element displaying the remaining time.
 * @param {number} secondsRemaining - The number of seconds remaining.
 * @returns {void}
 */
export function updateSecondRemainingText(remainingTimeElement, secondsRemaining) {
    const element = remainingTimeElement;
    if (secondsRemaining >= 0) {
        element.textContent = getTimeRemainingText(secondsRemaining);
    }
}

/**
 * Updates the display of the warning banner.
 *
 * @param {boolean} isWarning - Whether the warning should be displayed.
 * @param {Options} options - The banner element.
 * @returns {void}
 */
export function updateWarningDisplay(isWarning, options) {
    const { bannerElement } = options;

    if (isWarning && getFlag('hasClassHidden')) {
        bannerElement.classList.remove('out_of_hours_banner__hidden');
        bannerElement.removeAttribute('aria-hidden');
        setFlag('hasClassHidden', false);
    }
}

/**
 * Updates the display of the final warning banner.
 *
 * @param {boolean} isFinalWarning - Whether the final warning should be displayed.
 * @param {Options} options - The inner banner element.
 * @returns {void}
 */
export function updateFinalWarningDisplay(isFinalWarning, options) {
    const { innerBannerElement } = options;

    if (isFinalWarning && !getFlag('hasClassFinal')) {
        innerBannerElement.classList.add('out_of_hours_banner__closing_warning--final');
        setFlag('hasClassFinal', true);
    }
}

/**
 * Sets an element to hidden.
 *
 * @param {HTMLElement} element - The element to hide.
 * @returns {void}
 */
export function setElementToHidden(element) {
    element.classList.add('out_of_hours_banner__hidden');
    element.setAttribute('aria-hidden', 'true');
}

/**
 * Sets an element to visible.
 *
 * @param {HTMLElement} element - The element to show.
 * @returns {void}
 */
export function setElementToVisible(element) {
    element.classList.remove('out_of_hours_banner__hidden');
    element.removeAttribute('aria-hidden');
}

/**
 * Updates the display for last minute warning.
 *
 * @param {boolean} isLastMinute - Whether the service is in last minute (59 seconds or less).
 * @param {Options} options - The elements to update.
 * @returns {void}
 */
export function updateLastMinuteDisplay(isLastMinute, options) {
    const { warningText3Element, warningText4Element } = options;

    if (isLastMinute) {
        if (warningText3Element) {
            setElementToHidden(warningText3Element);
        }
        if (warningText4Element) {
            setElementToVisible(warningText4Element);
        }
    } else {
        if (warningText3Element) {
            setElementToVisible(warningText3Element);
        }
        if (warningText4Element) {
            setElementToHidden(warningText4Element);
        }
    }
}

/**
 * Updates the display when the service is closed.
 *
 * @param {boolean} isServiceClosed - Whether the service is closed.
 * @param {Options} options - The elements to update.
 * @returns {void}
 */
export function updateServiceClosedDisplay(isServiceClosed, options) {
    const {
        warningText1Element,
        warningText2Element,
        warningText3Element,
        warningText4Element,
        remainingTimeElement,
        closedTextElement,
        bannerTextElement,
    } = options;

    if (isServiceClosed) {
        if (warningText1Element) {
            setElementToHidden(warningText1Element);
        }
        if (warningText2Element) {
            setElementToHidden(warningText2Element);
        }
        if (warningText3Element) {
            setElementToHidden(warningText3Element);
        }
        if (warningText4Element) {
            setElementToHidden(warningText4Element);
        }
        if (remainingTimeElement) {
            setElementToHidden(remainingTimeElement);
        }
        if (closedTextElement) {
            setElementToVisible(closedTextElement);
        }
        if (bannerTextElement) {
            bannerTextElement.setAttribute('aria-live', 'polite');
        }
    }
}

/** @type {typeof setInterval | null} */
let interval = null;

/**
 * Checks the elements and updates their display based on the remaining time.
 *
 * @param {Options} options - The elements and times to check.
 * @returns {boolean} True if the interval should continue, otherwise false.
 */
export function checkElements(options) {
    if (!options) {
        return false;
    }

    const { remainingTimeElement, close, warningSeconds, finalWarningSeconds } = options;

    const secondsRemaining = getSecondsRemaining(close);
    const isWarning = secondsRemaining <= warningSeconds;
    const isFinalWarning = secondsRemaining <= finalWarningSeconds;
    const isLastMinute = secondsRemaining <= 59;
    const isServiceClosed = secondsRemaining <= 0;

    updateSecondRemainingText(remainingTimeElement, secondsRemaining);
    updateWarningDisplay(isWarning, options);
    updateFinalWarningDisplay(isFinalWarning, options);
    updateLastMinuteDisplay(isLastMinute, options);
    updateServiceClosedDisplay(isServiceClosed, options);

    return !isServiceClosed;
}

/**
 * Wraps the interval to clean up when checkElements returns false
 *
 * @param {Options} options
 * @returns {void}
 */
export function intervalCallback(options) {
    const shouldContinue = checkElements(options);
    if (!shouldContinue) {
        clearInterval(interval);
    }
}

/**
 * Sets up the interval to check and update the elements.
 *
 * @param {BannerElementsFromDOM} elements - The elements to check and update.
 * @returns {void}
 */
export function setupInterval(elements) {
    if (!elements) {
        return;
    }

    const { bannerElement, innerBannerElement, remainingTimeElement } = elements;

    setFlag('hasClassHidden', bannerElement.classList.contains('out_of_hours_banner__hidden'));
    setFlag('hasClassFinal', innerBannerElement.classList.contains('out_of_hours_banner__closing_warning--final'));

    /** @type {Options} */
    const options = {
        ...elements,
        ...getCloseAndWarningTimes(remainingTimeElement),
    };

    interval = setInterval(() => intervalCallback(options), CHECK_INTERVAL_MS);
}

/**
 * Initializes the script by retrieving elements and setting up the interval.
 * It will bail early if no banner element is found.
 *
 * @returns {void}
 */
export function initialise() {
    const elements = getElementsFromDom();
    if (elements == null) {
        return;
    }

    setupInterval(elements);
}

/**
 * @typedef {[
 *   className: string,
 *   eventType: string,
 *   handler: (event: Event) => void
 * ]} EventTuple
 */

/**
 * Forces the input to be numeric only.
 *
 * @param {Event} event - The input event.
 * @returns {void}
 */
export function forceInputNumeric(event) {
    const { target } = event;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    target.value = target.value.replace(/[^\d\s]/g, '');
    target.setSelectionRange(start, end);
}

/**
 * Forces the input to be numeric and hyphen only.
 *
 * @param {Event} event - The input event.
 * @returns {void}
 */
export function forceInputNumericAndHyphen(event) {
    const { target } = event;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    target.value = target.value.replace(/[^0-9-]/g, '');
    target.setSelectionRange(start, end);
}

/**
 * Forces the input to be alphanumeric only.
 *
 * @param {Event} event - The input event.
 * @returns {void}
 */
export function forceInputAlphnumeric(event) {
    const { target } = event;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    target.value = target.value.replace(/[^a-zA-Z\d\s]/g, '');
    target.setSelectionRange(start, end);
}

/**
 * Forces the input to be alphanumeric and allow left chevron.
 *
 * @param {Event} event - The input event.
 * @returns {void}
 */
export function forceInputAlphnumericLeftChevron(event) {
    const { target } = event;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    target.value = target.value.replace(/[^a-zA-Z<\d\s]/g, '');
    target.setSelectionRange(start, end);
}

/**
 * Forces the input to be uppercase.
 *
 * @param {Event} event - The input event.
 * @returns {void}
 */
export function forceInputUppercase(event) {
    const { target } = event;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    target.value = target.value.toLocaleUpperCase();
    target.setSelectionRange(start, end);
}

/**
 * Prevents spaces from being entered.
 *
 * @param {KeyboardEvent} event - The keypress event.
 * @returns {void}
 */
export function forceInputNoSpaces(event) {
    const { key } = event;

    if (key === ' ') {
        event.preventDefault();
    }
}

/**
 * Prevent the default event action, such as submit or paste.
 *
 * @param {Event} event - The submit or paste event, etc.
 * @returns {boolean} Returns false to prevent event propagation.
 */
export function preventEventDefault(event) {
    event.preventDefault();

    return false;
}

/**
 * Registers event listeners for elements with a specific class name.
 *
 * @param {string} className - The class name of the elements.
 * @param {string} type - The event type.
 * @param {EventListener} callback - The event listener callback.
 */
export function registerEventsByClassName(className, type, callback) {
    const elements = document.getElementsByClassName(className);

    for (const element of elements) {
        element.addEventListener(type, callback);
    }
}

/** @type {EventTuple[]} */
const events = [
    ['uppercase', 'input', forceInputUppercase],
    ['alphanumericOnly', 'input', forceInputAlphnumeric],
    ['alphanumericChevronInputs', 'input', forceInputAlphnumericLeftChevron],
    ['numericOnly', 'input', forceInputNumeric],
    ['numericAndHyphenOnly', 'input', forceInputNumericAndHyphen],
    ['noSpace', 'keypress', forceInputNoSpaces],
    ['noPaste', 'paste', preventEventDefault],
    ['disableOnSubmit', 'submit', preventEventDefault],
];

/**
 * Registers input listeners for the given elements
 *
 * @returns {void}
 */
export function registerInputListeners() {
    for (const [className, type, callback] of events) {
        registerEventsByClassName(className, type, callback);
    }
}

export default function openEvlInNewTab() {
    const continueButton = document.querySelector('#evl-continue-button');
    if (!continueButton) {
        return;
    }
    continueButton.addEventListener('click', (event) => {
        event.preventDefault();
        const isTaxVehicleChecked = document.querySelector(
            '#wizard_acquire_from_trade_capture_vehicle_tax_or_sorn_tax_or_sorn_decision'
        ).checked;
        if (isTaxVehicleChecked) {
            const url = 'https://www.gov.uk/vehicle-tax';
            window.open(url, '_blank');
            window.focus();
        }
    });
}

const initializeApplication = () => {
    // initialise form utils
    window.onload = () => {
        registerInputListeners();
        initialise();
        openEvlInNewTab();
    };
};

baseInitializeApplication(); // initialize base js
initializeApplication(); // initialize vehicle js
